import { useEffect, useState } from "react";
import { Col, Divider, Flex, Form, FormInstance, Typography } from "antd";
import dayjs from "dayjs";
import {
  DefaultDateInput,
  DefaultInput,
  FileInput,
  CustomButton,
  UploadedFileDisplay,
  WhiteBoxBlock,
  CountryCodeSelect,
  FiatCurrencySelect,
  NumberInput,
  UnkInput,
  RadioButtonGroup,
  DefaultSelect,
} from "ui";
import {
  CONTRACT_STATUS_ENUM,
  ContractEntity,
  CreateContractRequest,
  DIRECTION_TEXT,
  DirectionType,
  DocUploadedResponse,
  FILE_TYPE_ENUM,
  UnkValidationResponse,
} from "interfaces";
import {
  _isRequired,
  _isNumericWithPrecision,
  _isRequiredDate,
  _dateIsNotFuture,
  _greaterThan,
  _greaterOrEqual,
  renderBooleanSelectOptions,
} from "utils";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { ActionContractNotifier } from "components";
import { useStores } from "stores";
import styles from "./contractForm.module.css";

type InitialValues = Omit<CreateContractRequest, "date"> & {
  date: dayjs.Dayjs;
};

interface IProps {
  form: FormInstance<any>;
  isEditPage?: boolean;
  disabled: boolean;
  initValues?: InitialValues;
  onFinish: () => Promise<void>;
  contractInfo?: ContractEntity;
}

export const ContractForm = ({ form, initValues, onFinish, disabled, isEditPage, contractInfo }: IProps) => {
  const { fiatCurrencyList } = useStores();
  const [directionType, setDirectionType] = useState<DirectionType>(initValues?.direction || "IMP");
  const [contractFrame, setContractFrame] = useState<boolean>(initValues?.fiatAmount === 0);
  const [showHints, setShowHints] = useState(true);

  const directionOptions = Object.entries(DIRECTION_TEXT).map(([value, label]) => ({
    label,
    value: value as DirectionType,
  }));
  const [unkData, setUnkData] = useState<UnkValidationResponse | undefined>();
  const [uploadedFiles, setUploadedFiles] = useState<DocUploadedResponse[]>([]);

  const btnText = isEditPage ? "Сохранить" : "Зарегистрировать договор";

  const contractIsActive = contractInfo && contractInfo?.status === CONTRACT_STATUS_ENUM.ACTIVE;

  useEffect(() => {
    if (!fiatCurrencyList?.currencyList?.length) {
      fiatCurrencyList.onChangeDataFlow({}, { initialize: true });
    }
  }, []);

  const handleChangeFrame = (value: boolean) => {
    setContractFrame(value);
    if (value) {
      form.setFieldValue("fiatAmount", 0);
      form.validateFields(["fiatAmount"]);
    }
  };

  const handleUploadFile = async (responseUpload: DocUploadedResponse[]) => {
    const newFiles = [...uploadedFiles, ...responseUpload];

    setUploadedFiles(newFiles);

    form.setFieldValue(
      "docs",
      newFiles.map((item) => item.id)
    );
  };

  const handleRemoveDocument = (file: DocUploadedResponse) => {
    const filteredFiles = uploadedFiles.filter((item) => item.id !== file.id);
    setUploadedFiles(filteredFiles);

    form.setFieldValue("docs", filteredFiles.length > 0 ? filteredFiles.map((item) => item.id) : undefined);
  };

  const onUnkDataReceived = (bankData: UnkValidationResponse) => {
    setUnkData(bankData);

    if (isEditPage) {
      form.setFieldValue("bankBik", bankData.bankBik);
    } else {
      form.setFieldsValue({
        direction: bankData.direction,
        bankBik: bankData.bankBik,
      });
    }
  };

  const amountInput = (
    <div className={styles.amountInputContainer}>
      <Form.Item id={!isEditPage ? "contract-frame-checkbox" : undefined} className={styles.selectContainer}>
        <DefaultSelect value={contractFrame} onChange={handleChangeFrame} placeholder="Рамочный">
          {renderBooleanSelectOptions()}
        </DefaultSelect>
      </Form.Item>
      <Form.Item
        name="fiatAmount"
        rules={[
          _isRequired("Введите сумму договора"),
          contractIsActive ? _greaterThan(contractInfo.paidAmount) : _greaterOrEqual(0),
          _isNumericWithPrecision(12),
        ]}
        validateDebounce={FORM_VALIDATION_DEBOUNCE}
        className="flex-grow"
      >
        <NumberInput placeholder="Сумма договора" disabled={disabled || contractFrame} />
      </Form.Item>
    </div>
  );

  return (
    <Form
      form={form}
      name="contractForm"
      initialValues={{
        direction: directionType,
        docs: "",
        ...initValues,
      }}
      onFinish={onFinish}
    >
      <Flex vertical gap="large">
        <ActionContractNotifier form={form} unkData={unkData} />
        <WhiteBoxBlock className={styles.contractFormBlock}>
          <Flex align="center" gap="large" justify="space-between" className="margin-bottom-lg">
            {contractInfo?.status !== CONTRACT_STATUS_ENUM.ACTIVE && (
              <Flex vertical gap="middle">
                <Typography.Text strong>Направление</Typography.Text>
                <Form.Item name="direction" noStyle>
                  <RadioButtonGroup
                    options={directionOptions}
                    value={directionType}
                    onChange={(value) => setDirectionType(value)}
                    disabled={disabled}
                  />
                </Form.Item>
              </Flex>
            )}
          </Flex>
          <Flex vertical>
            {!contractIsActive && (
              <>
                <Flex className={styles.doubleInputRow}>
                  <Col id={!isEditPage ? "contract-unk-input" : undefined}>
                    <UnkInput inputProps={{ disabled }} onUnkDataReceived={onUnkDataReceived} showHints={showHints} />
                  </Col>
                  <Form.Item
                    name="date"
                    rules={[_isRequiredDate("Укажите дату заключения договора"), _dateIsNotFuture()]}
                    validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  >
                    <DefaultDateInput placeholder="Дата заключения" disabled={disabled} />
                  </Form.Item>
                </Flex>

                <Form.Item hidden name="bankBik" />
              </>
            )}

            {contractIsActive ? (
              <>{amountInput}</>
            ) : (
              <Flex className={styles.doubleInputRow}>
                {amountInput}
                <FiatCurrencySelect
                  name={"fiatCurrency"}
                  inputProps={{
                    disabled,
                  }}
                />
              </Flex>
            )}

            <Flex id={!isEditPage ? "contract-agent-input" : undefined} vertical>
              <Form.Item
                name="counterpartyName"
                rules={[_isRequired("Введите название контрагента")]}
                validateDebounce={FORM_VALIDATION_DEBOUNCE}
              >
                <DefaultInput placeholder="Контрагент" disabled={disabled} />
              </Form.Item>
              <Form.Item name="counterpartyCountry" hidden />
              <Flex className={styles.doubleInputRow}>
                <CountryCodeSelect
                  inputProps={{
                    disabled,
                  }}
                  name={"counterpartyCountryCode"}
                />
                <Flex vertical>
                  <Form.Item name="foreignOrganizationCode" noStyle>
                    <DefaultInput placeholder="Код иностранной организации" disabled={disabled} maxLength={50} />
                  </Form.Item>
                </Flex>
              </Flex>
            </Flex>
            <Form.Item name="counterpartyLegalAddress">
              <DefaultInput placeholder="Юридический адрес" disabled={disabled} maxLength={255} />
            </Form.Item>
            <Form.Item name="counterpartyActualAddress">
              <DefaultInput placeholder="Фактический адрес" disabled={disabled} maxLength={255} />
            </Form.Item>
          </Flex>
        </WhiteBoxBlock>

        {!isEditPage && (
          <WhiteBoxBlock className={styles.contractFormBlock}>
            <Typography.Title className="margin-top-none" level={2}>
              Скан договора
            </Typography.Title>
            <Flex id={!isEditPage ? "contract-document-input" : undefined} vertical gap="small">
              {uploadedFiles.length > 0 && (
                <Flex vertical gap="small">
                  {uploadedFiles.map((item) => (
                    <Flex justify="center" vertical key={item.id} className={`${styles.uploadedFileBlock}`}>
                      <UploadedFileDisplay
                        fileName={item.originalFilename}
                        onRemove={() => handleRemoveDocument(item)}
                        tooltip="Удалить документ"
                        key={item.id}
                      />
                    </Flex>
                  ))}
                </Flex>
              )}
              <FileInput
                fileType={FILE_TYPE_ENUM.CONTRACT}
                onUpload={handleUploadFile}
                multiple
                isContinue={uploadedFiles.length > 0}
              />
            </Flex>
          </WhiteBoxBlock>
        )}
      </Flex>
      <Form.Item>
        <Flex gap="large" className="margin-top-lg">
          <CustomButton
            id={!isEditPage ? "contract-save-button" : undefined}
            disabled={disabled || !(isEditPage || uploadedFiles.length > 0)}
            loading={disabled}
            htmlType="submit"
            tooltip={!(isEditPage || uploadedFiles.length > 0) ? "Загрузите скан договора" : undefined}
            onClick={() => {
              setShowHints(false);
            }}
          >
            {btnText}
          </CustomButton>
        </Flex>
      </Form.Item>
    </Form>
  );
};
